import { apiClient } from '@/common/lib/api-client';

/**
 * GET /api/v1/whatsapp/companies/{company_id}/flows/
 * Lists all WhatsApp Automation Flows for a given company.
 */
export async function getWhatsAppFlows(companyId, params = {}) {
  const { data } = await apiClient.get(
    `/v1/whatsapp/companies/${companyId}/flows/`,
    { params },
  );
  return data;
}

/**
 * POST /api/v1/whatsapp/companies/{company_id}/flows/
 * Creates a new WhatsApp Automation Flow.
 */
export async function createWhatsAppFlow({ companyId, data }) {
  const response = await apiClient.post(
    `/v1/whatsapp/companies/${companyId}/flows/`,
    data,
  );
  return response.data;
}

/**
 * GET /api/v1/whatsapp/companies/{company_id}/flows/{flow_id}/
 * Retrieves details of a specific WhatsApp Automation Flow.
 */
export async function getWhatsAppFlow({ companyId, flowId }) {
  const { data } = await apiClient.get(
    `/v1/whatsapp/companies/${companyId}/flows/${flowId}/`,
  );
  return data;
}

/**
 * PATCH /api/v1/whatsapp/companies/{company_id}/flows/{flow_id}/
 * Updates an existing WhatsApp Automation Flow.
 */
export async function updateWhatsAppFlow({ companyId, flowId, data }) {
  const response = await apiClient.patch(
    `/v1/whatsapp/companies/${companyId}/flows/${flowId}/`,
    data,
  );
  return response.data;
}

/**
 * DELETE /api/v1/whatsapp/companies/{company_id}/flows/{flow_id}/
 * Permanently deletes a WhatsApp Automation Flow and all associated steps and options.
 */
export async function deleteWhatsAppFlow({ companyId, flowId }) {
  await apiClient.delete(
    `/v1/whatsapp/companies/${companyId}/flows/${flowId}/`,
  );
}

/**
 * POST /api/v1/whatsapp/companies/{company_id}/flows/{flow_id}/steps/
 * Creates a new step in a specific WhatsApp Automation Flow.
 */
export async function createWhatsAppFlowStep({ companyId, flowId, data }) {
  const response = await apiClient.post(
    `/v1/whatsapp/companies/${companyId}/flows/${flowId}/steps/`,
    data,
  );
  return response.data;
}

/**
 * PATCH /api/v1/whatsapp/companies/{company_id}/flows/{flow_id}/steps/{step_id}/
 * Updates a specific step in a WhatsApp Automation Flow.
 */
export async function updateWhatsAppFlowStep({
  companyId,
  flowId,
  stepId,
  data,
}) {
  const response = await apiClient.patch(
    `/v1/whatsapp/companies/${companyId}/flows/${flowId}/steps/${stepId}/`,
    data,
  );
  return response.data;
}

/**
 * DELETE /api/v1/whatsapp/companies/{company_id}/flows/{flow_id}/steps/{step_id}/
 * Delete a specific step from a flow.
 */
export async function deleteWhatsAppFlowStep({ companyId, flowId, stepId }) {
  await apiClient.delete(
    `/v1/whatsapp/companies/${companyId}/flows/${flowId}/steps/${stepId}/`,
  );
}

/**
 * POST /api/v1/whatsapp/companies/{company_id}/flows/{flow_id}/steps/{step_id}/options/
 * Add a selection option / branch response mapping to a flow step.
 */
export async function createWhatsAppFlowOption({
  companyId,
  flowId,
  stepId,
  data,
}) {
  const response = await apiClient.post(
    `/v1/whatsapp/companies/${companyId}/flows/${flowId}/steps/${stepId}/options/`,
    data,
  );
  return response.data;
}

/**
 * DELETE /api/v1/whatsapp/companies/{company_id}/flows/{flow_id}/steps/{step_id}/options/{option_id}/
 * Delete a branching option choice from a step.
 */
export async function deleteWhatsAppFlowOption({
  companyId,
  flowId,
  stepId,
  optionId,
}) {
  await apiClient.delete(
    `/v1/whatsapp/companies/${companyId}/flows/${flowId}/steps/${stepId}/options/${optionId}/`,
  );
}

/**
 * POST /api/v1/whatsapp/companies/{company_id}/flows/media/upload/
 * Uploads a media file (image, video, document) for a flow step.
 */
export async function uploadWhatsAppFlowMedia({ companyId, file }) {
  const formData = new FormData();
  formData.append('file', file);
  const response = await apiClient.post(
    `/v1/whatsapp/companies/${companyId}/flows/media/upload/`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    },
  );
  return response.data;
}

function sanitizeJsonFilename(name) {
  const base =
    String(name || 'flow')
      .replace(/[<>:"/\\|?*]/g, '_')
      .trim() || 'flow';
  return /\.json$/i.test(base) ? base : `${base}.json`;
}

export function downloadJsonFile(data, filename) {
  const blob = new Blob([JSON.stringify(data, null, 2)], {
    type: 'application/json',
  });
  const blobUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = sanitizeJsonFilename(filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
}

/**
 * GET /api/v1/whatsapp/marketplace/industries/
 * Lists all active industries for marketplace filtering.
 */
export async function getMarketplaceIndustries(params = {}) {
  const { data } = await apiClient.get('/v1/whatsapp/marketplace/industries/', {
    params,
  });
  return data;
}

/**
 * GET /api/v1/whatsapp/marketplace/workflows/
 * Lists active marketplace automation workflows.
 */
export async function getMarketplaceWorkflows(params = {}) {
  const { data } = await apiClient.get('/v1/whatsapp/marketplace/workflows/', {
    params,
  });
  return data;
}

/**
 * GET /api/v1/whatsapp/marketplace/workflows/{workflow_id}/
 * Retrieves pre-fill payload for a single marketplace automation workflow.
 */
export async function getMarketplaceWorkflowDetail(workflowId) {
  const { data } = await apiClient.get(
    `/v1/whatsapp/marketplace/workflows/${workflowId}/`,
  );
  return data;
}
